import React, { ReactElement, useCallback, useEffect, Fragment } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useRouteMatch } from 'react-router-dom';

import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useAsyncDebounce,
    Column as ColumnType,
    TableOptions,
    UseSortByColumnOptions,
    UseFiltersColumnOptions,
} from 'react-table';
import { Paginated, PaginatedBaseMeta } from '../../../../../../../../services/api-handlers/pagination';
import Pagination from './Pagination';
import { QueryParams } from '../../../../../../../../types/grid';
import styles from './assets/grid.module.scss';
import textFilter from './filters/text-filter';
import useMemoCompare from './hooks/memo-compare';
import Loader from '../../../../../../../../../src/components/Loader';

import Dotdotdot from 'react-dotdotdot';
import { DateTimePicker, DropdownList } from 'react-widgets';
import user from '../../../../../../../../services/user-auth';
import UserRole from '../../../../../../../../enumerables/user-role';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faAngleDoubleRight, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { IStudent } from '../../../../../../../../types/student';
import CaseEnum from '../../../../../../../../enumerables/case-enum';
import { getValFromEnum } from '../../../../../../../../components/redux-form/utils/dropdown-values';

type ColumnAdd = {
    headerClassName?: string;
    noDotsRow?: boolean;
};

export type Column<T extends Record<string, unknown>> = ColumnType<T> &
    UseSortByColumnOptions<T> &
    ColumnAdd &
    UseFiltersColumnOptions<T>;

interface Props<T extends Record<string, unknown>> {
    columns: Column<T>[];
    columnOpen?: boolean;
    setColumnOpen?: (open: boolean) => void;
    noLoader?: boolean;
    filtersInit?: any;
    filterGlobal?: boolean;
    data: Paginated<T, PaginatedBaseMeta>;
    getData: (params: QueryParams) => void;
    getDataCsv?: (params: QueryParams) => void;
    noDots?: boolean;
    classNameGrid?: string;
    inProgressCSV?: boolean;
}

const defaultColumn = {
    Filter: textFilter(),
};
function GlobalFilter({ filtersInit, value, setValue }: any) {
    const onChange = useAsyncDebounce((change) => {
        setValue({ ...value, [change.id]: change.value });
    }, 200);

    const caseEnum = CaseEnum ? CaseEnum.getEnum('caseType') : {};
    const caseTypes: SelectObject[] = getValFromEnum(caseEnum);

    const dispatch = useDispatch();

    return (
        <div>
            <div className={classNames(styles.firstFilterBlock)}>
                <div className={classNames(styles.labelDiv)}>Active students for the period</div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span className={classNames(styles.labelDiv)}>From:</span>
                    <DateTimePicker
                        value={value && value.dateFrom ? moment(value.dateFrom).toDate() : undefined}
                        onChange={(date: any) => {
                            onChange({ id: 'dateFrom', value: date ? moment(date).format('YYYY-MM-DD') : undefined });
                        }}
                        time={false}
                        date={true}
                        format={'MMM D, YYYY'}
                    />
                </div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span className={classNames(styles.labelDiv)}>To:</span>
                    <DateTimePicker
                        value={value && value.dateTo ? moment(value.dateTo).toDate() : undefined}
                        onChange={(date: any) => {
                            onChange({ id: 'dateTo', value: date ? moment(date).format('YYYY-MM-DD') : undefined });
                        }}
                        time={false}
                        date={true}
                        format={'MMM D, YYYY'}
                    />
                </div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: '150px' }}>
                    <span className={classNames(styles.labelDiv)}>Case Type: </span>
                    <DropdownList
                        data={[{ label: 'All', value: '' }, ...caseTypes]}
                        containerClassName={'w-100'}
                        textField="label"
                        valueField="value"
                        onChange={(item: SelectObject) => {
                            onChange({ id: 'caseType', value: item.value ? item.value : undefined });
                        }}
                        value={value && value.caseType ? { value: value.caseType, label: '' } : undefined}
                    />
                </div>
            </div>
        </div>
    );
}
function Grid<T extends Record<string, unknown>>(props: Props<T>): ReactElement {
    const {
        columns,
        data,
        getData,
        columnOpen,
        setColumnOpen,
        filtersInit = [],
        getDataCsv,
        inProgressCSV,
        noLoader,
        noDots,
        classNameGrid,
    } = props;

    const [globalFilters, setGlobalFilters] = React.useState({
        // dateFrom: filtersInit.dateFrom,
        // dateTo: filtersInit.dateTo,
        //  dateTo: moment().endOf('month').format('YYYY-MM-DD'),
    });
    const match: any = useRouteMatch();
    // const filtersInit: any = [];

    useEffect(() => {
        if (
            (Object.values(match.params).length && match.params.status) ||
            match.params.status ||
            match.params.dateFrom ||
            match.params.dateTo
        ) {
            const arr = {
                dateTo: null,
                dateFrom: null,
            };
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: match.params[k] });
                if (k == 'dateTo') {
                    arr.dateTo = match.params[k];
                }
                if (k == 'dateFrom') {
                    arr.dateFrom = match.params[k];
                }
            });
            setGlobalFilters(arr);
        }
    }, []); // eslint-disable-line

    const instance = useTable<T>(
        {
            columns,

            defaultColumn,
            data: data.list,
            manualSortBy: true,
            filterTypes: true,
            manualFilters: true,
            manualPagination: true,
            pageCount: Math.max(1, data.totalPages ?? 1),
            initialState: {
                pageIndex: data.page - 1,
                pageSize: 20,
                filters: filtersInit,
            },
        } as TableOptions<T>,
        useFilters,
        useSortBy,
        usePagination,
    );
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = instance;

    const { sortBy, filters, pageIndex, pageSize } = state as any;
    const getPreparedFilters = useCallback(() => {
        const result: any = {};
        filters.forEach((filter: { id: string; value: any }) => {
            result[filter.id] = filter.value;
        });

        return result;
    }, [filters]);
    const getPreparedSorting = useCallback(() => {
        const result: any = {};
        sortBy.forEach((sort: { id: string; desc: boolean }) => {
            result[sort.id] = sort.desc ? 'desc' : 'asc';
        });

        return result;
    }, [sortBy]);
    const updatedAt = useMemoCompare(Date.now(), () => {
        return data.meta?.updatedAt !== 0;
    });

    useEffect(() => {
        const allSelectedFilters: any = { ...getPreparedFilters(), ...globalFilters };
        getData({
            page: pageIndex + 1,
            itemsPerPage: pageSize,
            filters: allSelectedFilters,
            sorting: getPreparedSorting(),
        });
    }, [getData, sortBy, filters, pageIndex, pageSize, updatedAt, globalFilters]); // eslint-disable-line

    const isProvider = user.getRoles().includes(UserRole.ROLE_USER);
    const isSupervisor = user.getSupervisor();

    return (
        <div className={'position-relative'}>
            {getDataCsv ? (
                isProvider && !isSupervisor ? null : (
                    <button
                        className={classNames(styles.btnHeader, 'btn btn-outline-info')}
                        onClick={() => {
                            getDataCsv({
                                page: pageIndex + 1,
                                // itemsPerPage: pageSize,
                                filters: { ...getPreparedFilters(), ...globalFilters },
                                sorting: getPreparedSorting(),
                            });
                        }}
                    >
                        <FontAwesomeIcon className={'mr-1'} icon={faDownload} />
                        {inProgressCSV ? 'loading...' : 'Export .csv'}{' '}
                    </button>
                )
            ) : null}
            {props.filterGlobal && (
                <GlobalFilter filtersInit={filtersInit} value={globalFilters} setValue={setGlobalFilters} />
            )}
            <div
                style={noDots ? {} : { minHeight: '450px' }}
                className={classNames(styles.table, 'table-responsive', classNameGrid)}
            >
                {data.inProgress && !noLoader ? <Loader /> : null}
                <table className={classNames('table')} {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup, ind) => (
                            <Fragment key={ind}>
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column: any, i: number) => {
                                        if (i === headerGroup.headers.length - 1 && setColumnOpen) {
                                            return (
                                                <th
                                                    style={{ position: 'relative' }}
                                                    key={i}
                                                    {...column.getHeaderProps()}
                                                    width={column.width}
                                                >
                                                    <span style={{ position: 'absolute', bottom: '5px' }}>
                                                        {column.render('Header')}
                                                    </span>
                                                    <button
                                                        style={{
                                                            float: 'right',
                                                        }}
                                                        className={'btn btn-info ml-3'}
                                                        onClick={() => {
                                                            setColumnOpen(!columnOpen);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={columnOpen ? faAngleDoubleLeft : faAngleDoubleRight}
                                                        />
                                                    </button>
                                                </th>
                                            );
                                        }
                                        return (
                                            <th key={i} {...column.getHeaderProps()} width={column.width}>
                                                {column.canSort ? (
                                                    <span
                                                        className={column.headerClassName}
                                                        {...column.getSortByToggleProps()}
                                                    >
                                                        {column.render('Header')}
                                                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                                    </span>
                                                ) : (
                                                    <span>{column.render('Header')}</span>
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    {headerGroup.headers.map((column: any, i: number) => (
                                        <th key={i} {...{ width: column.width }}>
                                            {column.canFilter ? column.render('Filter') : null}
                                        </th>
                                    ))}
                                </tr>
                            </Fragment>
                        ))}
                    </thead>
                    {noDots ? (
                        <tbody {...getTableBodyProps()}>
                            {data.list.length > 0 ? (
                                rows.map((row, i) => {
                                    if (row.index === 0 && row.original.totalRow) {
                                        prepareRow(row);
                                        return (
                                            <tr key={i} {...row.getRowProps()} className={classNames(styles.totalRow)}>
                                                {row.cells.map((cell, j: number) => {
                                                    return (
                                                        <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={i}
                                            {...row.getRowProps()}
                                            className={i % 2 < 1 ? classNames(styles.trOdd) : ''}
                                        >
                                            {row.cells.map((cell, j: number) => {
                                                return (
                                                    <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    {!data.inProgress && (
                                        <td colSpan={100} className={classNames(styles.empty, 'text-center', 'py-4')}>
                                            No results
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    ) : (
                        <tbody {...getTableBodyProps()}>
                            {data.list.length > 0 ? (
                                rows.map((row, i) => {
                                    if (row.index === 0 && row.original.totalRow) {
                                        prepareRow(row);
                                        return (
                                            <tr key={i} {...row.getRowProps()} className={classNames(styles.totalRow)}>
                                                {row.cells.map((cell, j: number) => {
                                                    return (
                                                        <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={i}
                                            {...row.getRowProps()}
                                            className={i % 2 < 1 ? classNames(styles.trOdd) : ''}
                                        >
                                            {row.cells.map((cell: any, j: number) => {
                                                //:Cell<T,any>

                                                const noDots: any = cell.column.noDotsRow;
                                                return (
                                                    <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                        {noDots ? (
                                                            cell.render('Cell')
                                                        ) : (
                                                            <Dotdotdot clamp={3}>{cell.render('Cell')}</Dotdotdot>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    {!data.inProgress && (
                                        <td colSpan={100} className={classNames(styles.empty, 'text-center', 'py-4')}>
                                            No results
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
                <Pagination<T> table={instance} />
            </div>
        </div>
    );
}

export default Grid;
